// Problems from a save (server 422 or the client mirror) pinned to schematic cards.
import { hasPhrase, placeholdersOf, t, type StaffLanguage, type Vars } from './staffText'
import type { OperationDoc, ProfileProblem } from './types'

export type PathStep = { key: string; index?: number }

export function parsePath(path: string): PathStep[] {
  if (!path) return []
  return path.split('.').map((part) => {
    const m = /^(.*)\[(\d+)\]$/.exec(part)
    return m ? { key: m[1]!, index: Number(m[2]) } : { key: part }
  })
}

const joinPath = (steps: readonly PathStep[]) =>
  steps.map((s) => (s.index === undefined ? s.key : `${s.key}[${s.index}]`)).join('.')

export type CardRef = { kind: 'group' | 'segment' | 'queue' | 'work' | 'doc'; id: string; field: string }

/** The deepest card the path lands on in `sent`, and the field under it; 'doc' when no card holds it. */
export function cardOf(path: string, sent: OperationDoc): CardRef {
  const steps = parsePath(path)
  const doc: CardRef = { kind: 'doc', id: 'doc', field: path }
  const [top, next] = steps
  if (!top || top.index === undefined) return doc
  const rest = (n: number) => joinPath(steps.slice(n))
  if (top.key === 'groups') {
    const g = sent.groups[top.index]
    if (!g) return doc
    const s = next?.key === 'segments' && next.index !== undefined ? g.segments[next.index] : undefined
    return s ? { kind: 'segment', id: s.id, field: rest(2) } : { kind: 'group', id: g.id, field: rest(1) }
  }
  if (top.key === 'queues') {
    const q = sent.queues[top.index]
    return q ? { kind: 'queue', id: q.id, field: rest(1) } : doc
  }
  if (top.key === 'workProfiles') {
    const w = sent.workProfiles[top.index]
    return w ? { kind: 'work', id: w.id, field: rest(1) } : doc
  }
  return doc
}

/**
 * Problems by card id ('doc' for the rest). Paths are index-based, so they are read
 * against the document as sent, not as it is now.
 */
export function pinProblems(problems: readonly ProfileProblem[], sent: OperationDoc): Map<string, ProfileProblem[]> {
  const pins = new Map<string, ProfileProblem[]>()
  for (const p of problems) {
    const { id } = cardOf(p.path, sent)
    pins.set(id, [...(pins.get(id) ?? []), p])
  }
  return pins
}

/** Mirrors explain(): English reads the server's sentence; az/ru the phrase when every placeholder is given. */
export function problemText(p: ProfileProblem, lang: StaffLanguage): string {
  if (lang === 'en') return p.message
  const key = `profiles.problem.${p.code}`
  if (!hasPhrase(key)) return p.message
  const vars: Vars = {}
  for (const [name, value] of Object.entries(p.args ?? {})) {
    if (value == null) continue
    vars[name] = typeof value === 'number' ? value : String(value) // a number keeps choosing the plural
  }
  if (!placeholdersOf(key).every((name) => name in vars)) return p.message
  return t(key, lang, vars)
}
